import React from 'react';
import { ShoppingCart, TrendingUp, Users, Search } from 'lucide-react';
import FeaturePage from './FeaturePage';

export default function TransferMarket() {
  return (
    <FeaturePage
      seo={{
        title: 'Transfer Market - Buy & Sell Players | My Football Game',
        description: 'Browse the transfer market in My Football Game. Buy players from other managers, sell your squad members, scout bargains and build your dream team through smart trading.',
        path: '/features/transfer-market',
      }}
      eyebrow="Feature Spotlight"
      title="TRANSFER "
      titleAccent="MARKET"
      subtitle="Buy and sell players with other managers worldwide. Scout bargains, flip players for profit, and build the strongest squad in the league through smart trading."
      breadcrumbLabel="Transfer Market"
      sections={[
        {
          icon: <Search className="w-5 h-5" />,
          title: 'Scout the Market',
          content: (
            <>
              <p>The transfer market in My Football Game is where managers list their players for sale and browse for reinforcements. Filter by position, age, price range, and overall rating to find the perfect signing for your squad.</p>
              <p>Every player has unique attributes — pace, shooting, passing, defending, and physical stats — so look beyond the overall rating to find hidden gems that fit your tactical system.</p>
            </>
          ),
        },
        {
          icon: <ShoppingCart className="w-5 h-5" />,
          title: 'Buy & Sell Players',
          content: (
            <>
              <p>List your own players on the market by setting an asking price in coins. Other managers can buy them instantly. Selling players you no longer need is one of the fastest ways to fund upgrades to your starting eleven.</p>
              <p>Quick-sell is available for instant coins if you need funds urgently, but listing on the open market usually earns a better price — especially for high-rated players in popular positions.</p>
            </>
          ),
        },
        {
          icon: <TrendingUp className="w-5 h-5" />,
          title: 'Trading Strategy',
          content: (
            <>
              <p>Smart managers treat the transfer market like a stock exchange. Buy undervalued players, train them to improve their stats, then sell at a higher price. This "flip" strategy can fund your squad's growth faster than match rewards alone.</p>
              <p>Watch for managers clearing out their squads — fire sales often produce bargains. Timing your purchases around tournament registrations can also save you coins when demand is lower.</p>
            </>
          ),
        },
        {
          icon: <Users className="w-5 h-5" />,
          title: 'Player-Driven Economy',
          content: (
            <>
              <p>Unlike games with randomly generated market listings, every player on the My Football Game transfer market is listed by a real manager. This creates a genuine economy where supply and demand drive prices.</p>
              <p>Goalkeepers and strikers tend to command the highest prices due to their direct impact on results. Midfield bargains are often the smartest investments for budget-conscious managers.</p>
            </>
          ),
        },
      ]}
      faqItems={[
        { q: 'How do I buy a player on the transfer market?', a: 'Open the Transfer Market tab, browse or search for the player you want, and click Buy. The player joins your squad instantly and the coins are deducted from your team budget.' },
        { q: 'Can I set any price when selling a player?', a: 'Yes, you choose the listing price in coins. Consider the player\'s overall rating, age, and position when pricing — overpriced players may sit on the market unsold.' },
        { q: 'What is quick-sell?', a: 'Quick-sell instantly converts a player into coins at a fixed rate based on their rating. It\'s faster than listing on the market but usually pays less than what another manager would offer.' },
        { q: 'Is the transfer market the same for all managers?', a: 'Yes, all managers share one global transfer market. Every player listed is visible to every manager worldwide, creating a competitive marketplace.' },
      ]}
      ctaTitle="Ready to build your dream squad?"
      ctaText="Sign up free and start scouting the transfer market today. No download required."
    />
  );
}
